import { Component, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProductDimensionInputsComponent } from 'src/app/components/product/dimension-inputs/dimension-inputs.component';
import { ProductService } from 'src/app/services/product/product.service';
import { Chart } from 'src/app/types/chart';
import { MeasurementService } from 'src/app/services/measurement/measurement.service';
import { DimensionService } from 'src/app/services/dimension/dimension.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { Category } from 'src/app/types/category';
import { ChartContainerComponent } from '../charts/container/container.component';
import { ChartService } from 'src/app/services/chart/chart.service';
import { ProductVariantService } from 'src/app/services/product-variant/product-variant.service';
import cloneDeep from 'lodash.clonedeep';
import { GradingComponent } from 'src/app/components/product/grading/grading.component';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { ProductInfoLayoutComponent } from '../layout/product-info/product-info.component';
import { MatButtonModule } from '@angular/material/button';

@Component({
  templateUrl: './chart.component.html',
  standalone: true,
  selector: 'app-onboarding-chart',
  imports: [
    ProductInfoLayoutComponent,
    MatButtonModule,
    ProductDimensionInputsComponent,
    ChartContainerComponent,
    GradingComponent,
    TranslatePipe,
  ],
  styles: `
    button[mat-button],
    button[mat-flat-button] {
      --mdc-filled-button-container-height: 36px;
      --mdc-text-button-container-height: 36px;
    }
  `,
})
export class ChartComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly productService = inject(ProductService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly measurementService = inject(MeasurementService);
  private readonly dimensionService = inject(DimensionService);
  private readonly categoryService = inject(CategoryService);
  private readonly chartService = inject(ChartService);
  private readonly productVariantService = inject(ProductVariantService);

  readonly productId = input.required<string>();
  readonly baseChart = input.required<Chart>({ alias: 'chart' });

  readonly back = output<void>();
  readonly continue = output<void>();

  readonly fetching = signal(true);
  readonly saving = signal(false);
  readonly chart = signal<Chart | undefined>(undefined);

  ngOnInit() {
    const category = this.baseChart().category as Category;

    this.measurementService.measurements = this.baseChart().measurements;
    this.categoryService.selectedCategory = category;
    this.dimensionService.categoryDimensions = category.dimensions ?? [];
    this.dimensionService.visibility = this.baseChart().measurementVisibility[category._id] ?? {};

    this.sub$ = this.chartService.chart$.subscribe((chart) => {
      if (!chart) return;

      this.chart.set(chart as Chart);

      this.categoryService.selectedCategory = (chart as Chart).category as Category;
    });

    this.sub$ = this.productService.getProduct(this.productId()).subscribe({
      next: ({ product }) => {
        this.productService.selectedProduct = product;
        // GOO-369: these used to arrive as a side effect of `ProductInfoLayoutComponent`, which
        // this step shares with the product page. The layout no longer owns variant state, so the
        // step that knows it is editing a chart for exactly one product sets them itself. This
        // onboarding chart is always brand new and always tied to that product, so unlike the
        // product page there is no version to gate on.
        this.productVariantService.variants = product.variants;
        this.productVariantService.selectedOption =
          product.variants.length > 1 ? (product.variants[0]?.selectedOptions?.[0]?.name ?? '_all') : '_all';
        this.fetching.set(false);
        setTimeout(() => {
          this.productVariantService.selectedVariants = [product.variants[0]];
        });
      },
      error: (error) => {
        captureException(error);
        this.snackbar.open(
          this.translate.instant(_('error.error-occured')),
          this.translate.instant(_('common.close')),
          { panelClass: 'snackbar-error' },
        );
      },
    });

    this.sub$ = this.measurementService.measurements$.subscribe((measurements) => {
      const chart = this.chartService.chart;

      if (!chart) return;

      const newMeasurements = cloneDeep(measurements);

      for (const variant in newMeasurements)
        for (const measurement in newMeasurements[variant])
          newMeasurements[variant][measurement] = newMeasurements[variant][measurement].toString();

      (chart as Chart).measurements = newMeasurements as Record<string, Record<string, string>>;
      this.chartService.chart = chart;
    });
  }

  override ngOnDestroy(): void {
    this.chartService.chart = undefined;

    super.ngOnDestroy();
  }

  onContinue() {
    this.saving.set(true);

    const chart = this.chartService.chart as Chart | undefined;

    if (!chart) return;

    this.sub$ = this.chartService
      .updateChart(this.baseChart()._id, {
        published: chart.published,
        measurements: chart.measurements,
        guide: chart.guide,
        labels: chart.labels,
        matching: chart.matching,
        model: chart.model,
        sizeRecommender: chart.sizeRecommender,
        optionNames: chart.optionNames,
        category: (chart.category as Category)._id,
        name: chart.name,
        measurementVisibility: chart.measurementVisibility,
        showUnitConversion: chart.showUnitConversion,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.continue.emit();
        },
        error: (error) => {
          captureException(error);
          this.saving.set(false);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            { panelClass: 'snackbar-error' },
          );
        },
      });
  }
}
